import React, { useRef, useEffect } from "react";

type OtpPinInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  /** Optional per-box style overrides for dark/light auth screens. */
  boxStyle?: React.CSSProperties;
};

/**
 * Native 6-box OTP input. Replaces react-pin-input (breaks on React 19).
 */
export function OtpPinInput({
  length = 6,
  value,
  onChange,
  boxStyle,
}: OtpPinInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(length, " ").slice(0, length).split("");

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const emit = (next: string[]) => {
    onChange(next.join("").replace(/\s/g, "").slice(0, length));
  };

  const handleChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      const next = [...digits];
      next[index] = " ";
      emit(next);
      return;
    }

    const chars = cleaned.split("");
    const next = [...digits];
    let cursor = index;
    for (const ch of chars) {
      if (cursor >= length) break;
      next[cursor] = ch;
      cursor += 1;
    }
    emit(next);
    if (cursor < length) {
      inputsRef.current[cursor]?.focus();
    } else {
      inputsRef.current[length - 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...digits];
      if (digits[index].trim()) {
        next[index] = " ";
        emit(next);
      } else if (index > 0) {
        next[index - 1] = " ";
        emit(next);
        inputsRef.current[index - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    const next = Array.from({ length }, (_, i) => pasted[i] || " ");
    emit(next);
    const focusAt = Math.min(pasted.length, length - 1);
    inputsRef.current[focusAt]?.focus();
  };

  return (
    <div
      className="ex-otp-pin"
      style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 8 }}
      onPaste={handlePaste}
    >
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digits[index].trim()}
          aria-label={`OTP digit ${index + 1}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          style={{
            width: 40,
            height: 50,
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.15)",
            background: "rgba(255,255,255,0.05)",
            color: "#fff",
            fontSize: 20,
            fontWeight: 600,
            textAlign: "center",
            outline: "none",
            ...boxStyle,
          }}
        />
      ))}
    </div>
  );
}
